import React, { useRef, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { bannerData } from '../../data/banner'
import { FaArrowRight, FaArrowLeft, FaClock, FaStar, FaUsers, FaPlay } from 'react-icons/fa'

const Banner = () => {
    const scrollRef = useRef(null)
    const [scrollDirection, setScrollDirection] = useState('right')
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isHovered, setIsHovered] = useState(false)

    // Responsive scroll amount based on viewport
    const getScrollAmount = () => {
        return window.innerWidth < 640 ? window.innerWidth * 0.9 :
            window.innerWidth < 1024 ? window.innerWidth * 0.48 :
                window.innerWidth * 0.45
    }

    useEffect(() => {
        const container = scrollRef.current
        let scrollInterval

        if (!isHovered) {
            scrollInterval = setInterval(() => {
                if (container) {
                    const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10
                    const isAtStart = container.scrollLeft === 0

                    if (isAtEnd) {
                        setScrollDirection('left')
                        setCurrentIndex(0)
                        container.scrollTo({ left: 0, behavior: 'smooth' })
                    } else if (isAtStart || scrollDirection === 'right') {
                        setScrollDirection('right')
                        container.scrollBy({ left: getScrollAmount(), behavior: 'smooth' })
                        setCurrentIndex(prev => prev + 1)
                    }
                }
            }, 4000)
        }

        return () => clearInterval(scrollInterval)
    }, [isHovered, scrollDirection])

    const manualScroll = (direction) => {
        const container = scrollRef.current
        if (container) {
            const scrollAmount = getScrollAmount()
            container.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            })
            setScrollDirection(direction)
            setCurrentIndex(prev => direction === 'left' ? Math.max(0, prev - 1) : prev + 1)
        }
    }

    return (
        <section className='min-h-[80vh] w-full relative overflow-hidden pt-12 bg-gray-50'>
            {/* Enhanced Background */}
            <div className='absolute inset-0 bg-[radial-gradient(circle_at_center,#9333ea_0%,#3b82f6_50%,transparent_75%)] opacity-10 z-0'></div>

            <div className='relative z-10 mx-auto'>
                {/* Progress Dots */}
                <div className='flex justify-center gap-2 mb-6'>
                    {bannerData.map((_, idx) => (
                        <motion.div
                            key={idx}
                            className={`h-2 w-2 rounded-full ${currentIndex === idx ? 'bg-violet-600' : 'bg-gray-300'}`}
                            animate={{ scale: currentIndex === idx ? 1.2 : 1 }}
                            transition={{ duration: 0.3 }}
                        />
                    ))}
                </div>

                <div className='relative'>
                    {/* Navigation Buttons */}
                    <AnimatePresence>
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            className='absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white p-2 sm:p-3 rounded-full shadow-lg backdrop-blur-md hidden sm:block'
                            onClick={() => manualScroll('left')}
                        >
                            <FaArrowLeft className="text-violet-600 text-lg sm:text-xl" />
                        </motion.button>
                    </AnimatePresence>

                    <div
                        ref={scrollRef}
                        className='flex overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar py-4'
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        {[...bannerData].map((item, index) => (
                            <motion.div
                                key={`${item.id}-${index}`}
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5 }}
                                className="flex-none mx-2 sm:mx-4 w-[100vw] sm:w-[47vw] lg:w-[44vw] snap-center"
                            >
                                <div className='relative h-[300px] sm:h-[360px] lg:h-[420px] rounded-2xl overflow-hidden shadow-xl transition-all duration-300 hover:shadow-2xl'>
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className='w-full h-full object-cover transition-transform duration-500 group-hover:scale-105'
                                        loading='lazy'
                                    />
                                    <div className='absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-4 sm:p-6 flex flex-col justify-between'>
                                        {/* Top Category Tag */}
                                        <motion.span
                                            initial={{ opacity: 0, y: -20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            className='inline-block px-3 py-1 bg-violet-600/90 text-white rounded-full text-xs sm:text-sm font-medium w-fit'
                                        >
                                            {item.category}
                                        </motion.span>

                                        {/* Bottom Content */}
                                        <div>
                                            <h3 className='text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 line-clamp-2'>{item.title}</h3>
                                            <p className='text-gray-200 text-xs sm:text-sm lg:text-base line-clamp-2 mb-4'>{item.description}</p>
                                            <div className='flex flex-wrap gap-3 sm:gap-4 text-green-200 text-xs sm:text-sm'>
                                                <span className='flex items-center gap-1.5'>
                                                    <FaClock /> {item.duration}
                                                </span>
                                                <span className='flex items-center gap-1.5'>
                                                    <FaStar className="text-yellow-400" /> {item.rating}
                                                </span>
                                                <span className='flex items-center gap-1.5'>
                                                    <FaUsers className="text-blue-400" /> {item.enrolled}
                                                </span>
                                            </div>
                                            <div className='mt-4 flex items-center justify-between gap-3'>
                                                <Link to={item.path}>
                                                    <motion.button
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                        className='flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-4 sm:px-6 py-2 rounded-lg font-medium transition-all duration-300'
                                                    >
                                                        <FaPlay className="text-sm" />
                                                        {item.buttonText}
                                                    </motion.button>
                                                </Link>
                                                <span className='text-lg sm:text-xl lg:text-2xl font-bold text-white'>{item.price}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <AnimatePresence>
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            className='absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white p-2 sm:p-3 rounded-full shadow-lg backdrop-blur-md hidden sm:block'
                            onClick={() => manualScroll('right')}
                        >
                            <FaArrowRight className="text-violet-600 text-lg sm:text-xl" />
                        </motion.button>
                    </AnimatePresence>
                </div>
            </div>

            {/* Mobile Navigation */}
            <div className='flex justify-center gap-4 mt-4 sm:hidden'>
                <button
                    onClick={() => manualScroll('left')}
                    className='bg-violet-100 p-2 rounded-full'
                >
                    <FaArrowLeft className="text-violet-600" />
                </button>
                <button
                    onClick={() => manualScroll('right')}
                    className='bg-violet-100 p-2 rounded-full'
                >
                    <FaArrowRight className="text-violet-600" />
                </button>
            </div>
        </section>
    )
}

export default Banner